// ============================================================================
// Central re-export hub for CKEditor 5 plugins & types used in the Arbo build.
// Keep this file lean: only re-export what's actually needed externally.
// (Helps tree-shaking when consumers use ESM + bundlers.)
// ============================================================================

// ----------------------------------------
// CORE EDITOR & BASE TYPES
// ----------------------------------------
export {
  Editor,
  Command,
  Plugin,
  CommandCollection,
} from "@ckeditor/ckeditor5-core";
export type { EditorConfig } from "@ckeditor/ckeditor5-core";

// Engine model/view primitives & writers (typed re-exports)
export {
  Model,
  ViewElement,
  ViewDowncastWriter,
} from "@ckeditor/ckeditor5-engine";
export type { UpcastConversionApi } from "@ckeditor/ckeditor5-engine";

// Widget helpers
export { Widget, toWidget } from "@ckeditor/ckeditor5-widget";

// Editors
export { BalloonEditor } from "@ckeditor/ckeditor5-editor-balloon";

// ----------------------------------------
// LEGACY / DEPRECATED TYPE PATCHES
// ----------------------------------------
/**
 * Legacy placeholder types retained for backward compatibility with earlier public API surface.
 * These are no longer exported by CKEditor 5 v46+; keep as `unknown` to avoid encouraging `any`.
 */
export type Writer = unknown;
export type Node = unknown;
export type Element = unknown;
export type ItemRenderer = unknown; // @deprecated

// ========================================
// ESSENTIAL PLUGINS
// ========================================
import { Essentials } from "@ckeditor/ckeditor5-essentials";
import { Paragraph } from "@ckeditor/ckeditor5-paragraph";
export { Essentials, Paragraph };

// ========================================
// TEXT FORMATTING
// ========================================
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Superscript,
  Subscript,
  Code,
} from "@ckeditor/ckeditor5-basic-styles";
import { Font } from "@ckeditor/ckeditor5-font";
import { Highlight } from "@ckeditor/ckeditor5-highlight";
import { AutoLink } from "@ckeditor/ckeditor5-link";
export {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Superscript,
  Subscript,
  Code,
  Font,
  Highlight,
  AutoLink,
};
// ========================================
// STRUCTURAL ELEMENTS
// ========================================
import { Heading } from "@ckeditor/ckeditor5-heading";
import { BlockQuote } from "@ckeditor/ckeditor5-block-quote";
import { List, TodoList } from "@ckeditor/ckeditor5-list";
export { Heading, BlockQuote, List, TodoList };

// ========================================
// MEDIA AND IMAGES
// ========================================
import {
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  ImageResize,
  AutoImage,
  ImageInsert,
} from "@ckeditor/ckeditor5-image";
import { EasyImage } from "@ckeditor/ckeditor5-easy-image";
import { MediaEmbed } from "@ckeditor/ckeditor5-media-embed";
export {
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  ImageResize,
  AutoImage,
  ImageInsert,
  EasyImage,
  MediaEmbed,
};

// ========================================
// UPLOAD FUNCTIONALITY
// ========================================
import { CKFinderUploadAdapter } from "@ckeditor/ckeditor5-adapter-ckfinder";
import { CKFinder } from "@ckeditor/ckeditor5-ckfinder";
import { SimpleUploadAdapter } from "@ckeditor/ckeditor5-upload";
export { CKFinderUploadAdapter, CKFinder, SimpleUploadAdapter };

// ========================================
// TABLE FUNCTIONALITY
// ========================================
import { Table, TableToolbar } from "@ckeditor/ckeditor5-table";
export { Table, TableToolbar };

// ========================================
// LINKS AND MENTIONS
// ========================================
import {  Link } from "@ckeditor/ckeditor5-link";
import { Mention } from "@ckeditor/ckeditor5-mention";
export { Link, Mention };
export type { MentionFeedItem } from "@ckeditor/ckeditor5-mention";
export type { MentionFeedbackCallback } from "@ckeditor/ckeditor5-mention";

// ========================================
// LAYOUT AND ALIGNMENT
// ========================================
import { Alignment } from "@ckeditor/ckeditor5-alignment";
import { Indent, IndentBlock } from "@ckeditor/ckeditor5-indent";
export { Alignment, Indent, IndentBlock };

// ========================================
// EDITING OPERATIONS
// ========================================
import { Delete } from "@ckeditor/ckeditor5-typing";
import { Undo, UndoEditing } from "@ckeditor/ckeditor5-undo";
import { Clipboard } from "@ckeditor/ckeditor5-clipboard";
import { PasteFromOffice } from "@ckeditor/ckeditor5-paste-from-office";
export { Delete, Undo, UndoEditing, Clipboard, PasteFromOffice };

// ========================================
// AUTOMATION AND UTILITIES
// ========================================
import { Autoformat } from "@ckeditor/ckeditor5-autoformat";
import { Autosave } from "@ckeditor/ckeditor5-autosave";
import { KeystrokeHandler } from "@ckeditor/ckeditor5-utils";
import { WordCount } from "@ckeditor/ckeditor5-word-count";

export { Autoformat, Autosave, KeystrokeHandler, WordCount };

// ========================================
// SPECIAL CHARACTERS AND EMOJI
// ========================================
// Note: Emoji plugin might require separate package installation
// export { Emoji } from "@ckeditor/ckeditor5-emoji"; // Uncomment if package is installed

// ========================================
// ENGINE AND WIDGET FUNCTIONALITY
// ========================================
// (Widget already re-exported above.)

// ========================================
// CONVENIENCE PLUGIN GROUPS (consumer helper arrays)
// ========================================
// Each array is a readonly tuple of plugin constructors so users can spread into config.

export const ARBO_MINIMAL_PLUGINS = [Essentials, Paragraph];

export const ARBO_TEXT_FORMATTING_PLUGINS = [
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Superscript,
  Subscript,
  Code,
  Font,
  Highlight,
  Alignment,
  Indent,
  IndentBlock,
];

export const ARBO_LIST_PLUGINS = [Heading, BlockQuote, List, TodoList];

export const ARBO_MEDIA_PLUGINS = [
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  ImageResize,
  AutoImage,
  ImageInsert,
  EasyImage,
  MediaEmbed,
];

export const ARBO_UPLOAD_PLUGINS = [
  CKFinderUploadAdapter,
  CKFinder,
  SimpleUploadAdapter,
];

export const ARBO_TABLE_PLUGINS = [Table, TableToolbar];

export const ARBO_LINK_MENTION_PLUGINS = [Link, AutoLink, Mention];

export const ARBO_EDITING_UTILITY_PLUGINS = [
  Delete,
  Undo,
  UndoEditing,
  Clipboard,
  PasteFromOffice,
  Autoformat,
  Autosave,
  WordCount,
];

/**
 * A comprehensive set of recommended plugins for a rich editing experience.
 * Uses a Set to prevent duplicates if plugins are included in multiple groups.
 */
export const ARBO_RECOMMENDED_PLUGINS = [
  ...new Set([
    ...ARBO_MINIMAL_PLUGINS,
    ...ARBO_TEXT_FORMATTING_PLUGINS,
    ...ARBO_LIST_PLUGINS,
    ...ARBO_MEDIA_PLUGINS,
    ...ARBO_UPLOAD_PLUGINS,
    ...ARBO_TABLE_PLUGINS,
    ...ARBO_LINK_MENTION_PLUGINS,
    ...ARBO_EDITING_UTILITY_PLUGINS,
  ]),
];

// ========================================
// NOTES
// ========================================
// 1. Keep additions backwards-compatible: do not rename existing exports.
// 2. When adding new plugin groups, append (do not reorder) to avoid churn in consumer diffs.
// 3. Arrays are frozen via readonly tuples—spread them when configuring the editor.
